import { blendColors, contrastSafeStops, logoHaloSpec } from "@stanza/shared/color-contrast";

/** Past events render under this filter, which costs a saturated mark contrast. */
export const PAST_EVENT_GRAYSCALE = 0.8;

/** Separation carried while nothing is known about the mark. */
export const PENDING_LOGO_FILTER = "drop-shadow(rgba(0, 0, 0, 0.6) 2px 2px 7px)";

/**
 * Corrects a gradient's stops so a logo drawn over it stays legible.
 *
 * @param {string[]} stops - Gradient stop colors, in order.
 * @param {object | null} ink - Ink profile from `extractImageColors`.
 * @param {{ grayscale?: number, minRatio?: number }} [options]
 * @returns {{ stops: string[], ratio: number }} The input untouched, and a ratio
 *   of 1, when there is no ink profile to judge it against.
 */
export const contrastSafeGradientStops = (stops, ink, options = {}) => {
  if (!ink || !stops?.length) return { stops: stops ?? [], ratio: 1 };
  return contrastSafeStops(stops, ink, options);
};

/**
 * Builds the logo's CSS filter from the treatment the corrected gradient
 * implies: none when the field already carries the mark, otherwise a symmetric
 * halo in the opposite polarity.
 *
 * @param {string[]} stops - Gradient stops as painted.
 * @param {object | null} ink - Ink profile of the logo drawn over them.
 * @param {{ grayscale?: number }} [options]
 * @returns {string} A CSS `filter` value.
 */
export const logoHaloFilter = (stops, ink, options = {}) => {
  if (!ink) return PENDING_LOGO_FILTER;
  const { color, opacity, blurPx } = logoHaloSpec(stops, ink, options);
  if (!opacity) return "none";
  return `drop-shadow(0 0 ${blurPx}px rgba(${hexToRgbTriplet(color)}, ${opacity}))`;
};

/**
 * The span of a two-stop ramp one side's logo sits over: its own stop through
 * the ramp's midpoint, which is where the other side's takes over.
 *
 * @param {string[]} stops - The two corrected stops, in paint order.
 * @param {"left" | "right"} side
 * @returns {string[]} The stops bounding that half.
 */
export const halfOfRamp = (stops, side) => {
  const [from, to] = stops ?? [];
  const middle = from && to ? blendColors(from, to, 0.5) : null;
  // Without two stops there is no half to take, and no stop worth naming one:
  // pass the ramp through so it is judged whole rather than against a gap.
  if (!middle) return (stops ?? []).filter(Boolean);
  return side === "left" ? [from, middle] : [middle, to];
};

/**
 * Splits a `#rrggbb` color into the `r, g, b` triplet an `rgba()` needs.
 *
 * @param {string} hex
 * @returns {string}
 */
const hexToRgbTriplet = (hex) => {
  const value = parseInt(hex.slice(1), 16);
  return `${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}`;
};
